// Staff.jsx — the people who mill, pack and deliver.
//
// Not the same thing as Users: an admin account signs into this portal, a
// staff member is assigned to orders and signs into the miller or rider app.
// They are separate tables and separate screens, and conflating them is how a
// delivery rider ends up with the price list.

import React, { useCallback, useEffect, useState } from "react";
import api from "../../../api/axios";
import {
  PageShell,
  DataTable,
  Button,
  Pill,
  Avatar,
  RowActions,
  Toggle,
  Filter,
  Modal,
  Field,
  PlusIcon,
} from "../component/AdminUi";

const ROLES = [
  { value: "miller_packer", label: "Miller / Packer", tone: "gold" },
  { value: "delivery", label: "Delivery", tone: "forest" },
];

const roleOf = (r) => ROLES.find((x) => x.value === r);

/* Failure is a real HTTP status with a { code, message } body — there is no
   status field inside the body any more, and a plain 403 carries only a code. */
const errText = (err, fallback) =>
  err.response?.data?.message || err.response?.data?.code || fallback;

const onDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const blank = {
  id: null,
  name: "",
  phone: "",
  role: "miller_packer",
  active: true,
  storeId: "",
};

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  /* ── loading ───────────────────────────────────────────────────────────── */

  const load = useCallback(async () => {
    setError("");
    try {
      /* An unknown ?role= is a 400 rather than an ignored filter, so the
         "all roles" case sends no parameter at all. */
      const url = roleFilter ? `/admin/staff?role=${roleFilter}` : "/admin/staff";
      const res = await api.get(url);
      setStaff(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (err) {
      setError(errText(err, "Could not load the staff list."));
    } finally {
      setLoading(false);
    }
  }, [roleFilter]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    /* A bare array, and the store's id is `id` — still the 24-hex id a staff
       row's storeId carries, so the two keep comparing like for like. */
    api
      .get("/admin/stores")
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setStores(list);
        const fallback = list.find((s) => s.isDefault) || list[0];
        if (fallback) setForm((f) => ({ ...f, storeId: f.storeId || fallback.id }));
      })
      .catch(() => setStores([]));
  }, []);

  const storeName = (id) =>
    stores.find((s) => String(s.id) === String(id))?.name ||
    stores.find((s) => s.isDefault)?.name ||
    "—";

  const shown = query.trim()
    ? staff.filter((s) => {
        const q = query.trim().toLowerCase();
        return (
          (s.name || "").toLowerCase().includes(q) ||
          (s.phone || "").includes(q)
        );
      })
    : staff;

  /* ── the form ──────────────────────────────────────────────────────────── */

  const openNew = () => {
    setForm({
      ...blank,
      storeId: stores.find((s) => s.isDefault)?.id || stores[0]?.id || "",
    });
    setProblem("");
    setOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      name: row.name || "",
      phone: row.phone || "",
      role: row.role || "miller_packer",
      active: row.isActive !== false,
      storeId: row.storeId || stores.find((s) => s.isDefault)?.id || "",
    });
    setProblem("");
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setForm(blank);
    setProblem("");
  };

  const save = async () => {
    const name = form.name.trim();
    const phone = form.phone.replace(/\D/g, "").slice(-10);

    if (!name) return setProblem("Give them a name.");
    if (!form.role) return setProblem("Pick a role.");
    if (phone && phone.length !== 10)
      return setProblem("A mobile number is 10 digits, or leave it blank.");

    setSaving(true);
    setProblem("");

    const body = {
      name,
      phone,
      role: form.role,
      active: form.active,
      ...(form.storeId ? { storeId: form.storeId } : {}),
    };

    try {
      if (form.id) {
        /* The numeric id off the row — the route is constrained to a long, so
           a stale 24-hex _id 404s at routing rather than at the handler. */
        await api.put(`/admin/staff/${form.id}`, body);
      } else {
        await api.post("/admin/staff", body);
      }
      close();
      load();
    } catch (err) {
      setProblem(errText(err, "That did not save."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (
      !window.confirm(
        `Remove ${row.name}? Orders they already worked on keep their name.`
      )
    )
      return;
    try {
      await api.delete(`/admin/staff/${row.id}`);
      load();
    } catch (err) {
      setError(errText(err, "Could not remove them."));
    }
  };

  const flipActive = async (row) => {
    const next = !(row.isActive !== false);
    /* Shown before the server has agreed, because a toggle that waits for a
       round trip reads as broken and gets clicked twice. */
    setStaff((list) =>
      list.map((s) => (s.id === row.id ? { ...s, isActive: next } : s))
    );
    try {
      /* The update is strictly partial — only what the body names is touched
         — so the toggle sends the toggle and nothing else. */
      await api.put(`/admin/staff/${row.id}`, { active: next });
    } catch (err) {
      setStaff((list) =>
        list.map((s) => (s.id === row.id ? { ...s, isActive: !next } : s))
      );
      setError(errText(err, "Could not change that."));
    }
  };

  /* ── render ────────────────────────────────────────────────────────────── */

  const activeCount = staff.filter((s) => s.isActive !== false).length;

  return (
    <PageShell
      title="Staff"
      count={
        loading && !staff.length
          ? "Loading…"
          : `${staff.length} on the books · ${activeCount} active` +
            (query ? ` · ${shown.length} matching` : "")
      }
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search name or number",
      }}
      actions={
        <>
          <Filter
            value={roleFilter}
            onChange={setRoleFilter}
            ariaLabel="Filter by role"
            options={[
              { value: "", label: "All roles" },
              ...ROLES.map((r) => ({ value: r.value, label: r.label })),
            ]}
          />
          <Button kind="primary" onClick={openNew}>
            <PlusIcon />
            Add staff
          </Button>
        </>
      }
    >
      <DataTable
        columns={["Name", "Phone", "Role", "Store", "Active", "Added", ""]}
        loading={loading}
        error={error}
        isEmpty={!loading && shown.length === 0}
        emptyTitle={
          query || roleFilter ? "Nobody matches that" : "No staff yet"
        }
        emptyHint={
          query || roleFilter
            ? "Try clearing the search or the role filter."
            : "Add the people who mill, pack and deliver."
        }
      >
        {shown.map((row) => {
          const role = roleOf(row.role);
          const on = row.isActive !== false;
          return (
            <tr key={row.id}>
              <td>
                <span className="ui-who">
                  <Avatar label={row.name} accent={row.role === "delivery"} />
                  <span className="ui-who__name">{row.name || "—"}</span>
                </span>
              </td>
              <td className="ui-num">
                {row.phone ? (
                  row.phone.replace(/^(\d{5})(\d{5})$/, "$1 $2")
                ) : (
                  <span className="ui-muted">not set</span>
                )}
              </td>
              <td>
                <Pill tone={role?.tone || "grey"}>
                  {role?.label || row.role || "—"}
                </Pill>
              </td>
              <td>{storeName(row.storeId)}</td>
              <td>
                <Toggle
                  on={on}
                  onChange={() => flipActive(row)}
                  label={on ? "Active — tap to stand down" : "Stood down — tap to activate"}
                />
              </td>
              <td className="ui-num">{onDate(row.createdAt)}</td>
              <td>
                <RowActions
                  onEdit={() => openEdit(row)}
                  onDelete={() => remove(row)}
                />
              </td>
            </tr>
          );
        })}
      </DataTable>

      <Modal
        open={open}
        title={form.id ? `Edit ${form.name}` : "Add staff"}
        onClose={close}
        footer={
          <>
            <Button onClick={close}>Cancel</Button>
            <Button kind="primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : form.id ? "Save changes" : "Add them"}
            </Button>
          </>
        }
      >
        {problem ? <div className="ui-alert ui-alert--error">{problem}</div> : null}

        <Field label="Name" required>
          <input
            className="ui-input"
            value={form.name}
            onChange={set("name")}
            placeholder="As it should appear on the order"
          />
        </Field>

        <Field label="Mobile" hint="Used to sign in to the miller or rider app.">
          <input
            className="ui-input"
            value={form.phone}
            onChange={set("phone")}
            inputMode="numeric"
            maxLength={10}
            placeholder="10 digits"
          />
        </Field>

        <Field label="Role" required>
          <select className="ui-select" value={form.role} onChange={set("role")}>
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </Field>

        {stores.length > 0 ? (
          <Field label="Store" hint="Where they work.">
            <select
              className="ui-select"
              value={form.storeId}
              onChange={set("storeId")}
            >
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.isDefault ? " (default)" : ""}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        <Field
          label="Active"
          hint="Someone stood down keeps their history but is not offered for new work."
        >
          <div style={{ paddingTop: 2 }}>
            <Toggle
              on={form.active}
              onChange={(v) => setForm((f) => ({ ...f, active: v }))}
              label="Active"
            />
          </div>
        </Field>
      </Modal>
    </PageShell>
  );
}
